import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  forwardRef,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { BigNumber } from "ethers";
import { formatUnits, parseUnits } from "ethers/lib/utils";
import { useEffect, useRef, useState } from "react";
import { useBalance, useNetwork, useSigner } from "wagmi";

import analyticsInstance from "../../config/analytics";
import { convertToDecimals } from "../../helpers/numbers";
import { approveErc20Upgrade, getErc20Allowance, upgradeErc20 } from "../../helpers/tokens";

function TokenUpgraderModal({
  account,
  tokenBalance,
  setRefetch,
  isOpen,
  onClose,
  defaultAmount,
  newOpen,
  setNewOpen,
}) {
  const [allowance, setAllowance] = useState(BigNumber.from(0));
  const [amount, setAmount] = useState(defaultAmount);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [showUnlocked, setShowUnlocked] = useState(false);
  const [showUpgraded, setShowUpgraded] = useState(false);
  const [txError, setTxError] = useState();

  const inputRef = useRef(null);
  const { chain } = useNetwork();
  const signer = useSigner();

  const {
    data: useBalanceData,
    isError: useBalanceErrored,
    isFetching: useBalanceLoading,
  } = useBalance({
    addressOrName: account,
    token: tokenBalance.erc20Address,
    watch: true,
  });

  const clearState = () => {
    setTxError(null);
    setShowUnlocked(false);
    setShowUpgraded(false);
  };

  const getAllowance = (chainId, signer, account, tokenBalance) => {
    getErc20Allowance(chainId, signer, account, tokenBalance.erc20Symbol, tokenBalance.tokenxSymbol)
      .then(setAllowance)
      .catch((e) => {
        // TODO: error handling
        analyticsInstance.track("AllowanceGetError", {
          account: account,
          erc20: tokenBalance.erc20Symbol,
          tokenx: tokenBalance.tokenxSymbol,
          error: e,
        });
      });
  };

  // hook for updating the amount when the default amount changes
  // this can happen when the modal is closed and reopened
  useEffect(() => {
    if (newOpen) {
      setAmount(defaultAmount);
      setNewOpen(false);
    }
  }, [defaultAmount, newOpen, setNewOpen]);

  // hook for keeping track of the allowance - TODO: create a wagmi hook for this?
  useEffect(() => {
    getAllowance(chain.id, signer.data, account, tokenBalance);
  }, [chain, signer, account, tokenBalance]);

  return (
    <Modal isOpen={isOpen} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          "Upgrade" {tokenBalance.erc20Symbol}&nbsp;➡️&nbsp;{tokenBalance.tokenxSymbol}
        </ModalHeader>

        <ModalBody>
          <Text fontSize="md">
            Upgrade your ERC20 tokens into Super Tokens, so that they can be used for streaming into BTFDCA.
          </Text>
          <Link display="inline-block" fontSize="xs" mb="2em" href="/faq#erc20-tokenx" isExternal>
            Check the FAQ to learn more about Super Tokens.
          </Link>

          {useBalanceData && (
            <>
              <Input
                type="number"
                size="lg"
                step={1}
                min={0}
                max={useBalanceData.formatted}
                onChange={(evt) => setAmount(parseUnits(evt.target.value, useBalanceData.decimals))}
                defaultValue={formatUnits(amount, useBalanceData.decimals)}
                disabled={isUnlocking || isUpgrading}
                ref={inputRef}
              />

              <Text my="0.5em">
                <Button
                  size="sm"
                  onClick={() => {
                    setAmount(useBalanceData.value);
                    inputRef.current.value = formatUnits(useBalanceData.value, useBalanceData.decimals);
                    analyticsInstance.track("TokenUpgradeAvailableBalanceClicked");
                  }}
                >
                  Use available balance
                </Button>
                &nbsp;of&nbsp;
                {useBalanceLoading ? (
                  <Spinner as="span" ml="1em" />
                ) : useBalanceErrored ? (
                  "ERROR"
                ) : (
                  useBalanceData.formatted
                )}
                &nbsp;{tokenBalance.erc20Symbol}.
              </Text>

              {allowance > 0 && (
                <Text mb="1em" fontSize="sm">
                  Current Allowance: {formatUnits(allowance, useBalanceData.decimals)} {tokenBalance.erc20Symbol}
                </Text>
              )}

              {showUnlocked && (
                <Alert status="success" variant="subtle">
                  <AlertIcon />
                  Approval completed. You can now upgrade your tokens.
                </Alert>
              )}

              {showUpgraded && (
                <Alert status="success" variant="subtle">
                  <AlertIcon />
                  Your tokens have been successfully upgraded to SuperTokens! You can now use them for streaming.
                </Alert>
              )}

              {txError && (
                <Alert status="error" variant="subtle">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>Sorry, something's wrong...</AlertTitle>
                    <AlertDescription>
                      Error upgrading your tokens. This can happen if the network is congested, and your gas was not
                      sufficient to pay for the transaction. Can you resubmit with more gas?
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
            </>
          )}
        </ModalBody>

        <ModalFooter py="1em">
          <Button
            colorScheme="green"
            size="md"
            disabled={
              isUnlocking || isUpgrading || amount.eq(0) || amount.gt(useBalanceData.value) || allowance.gte(amount)
            }
            isLoading={isUnlocking}
            onClick={async () => {
              // clear state
              clearState();

              // set loading
              setIsUnlocking(true);

              // unlock
              approveErc20Upgrade(chain.id, signer.data, tokenBalance.erc20Symbol, tokenBalance.tokenxSymbol, amount)
                .then((result) => {
                  setShowUnlocked(true);
                  setIsUnlocking(false);
                  getAllowance(account, tokenBalance); // refresh allowance
                })
                .catch((err) => {
                  setTxError(err);
                  setIsUnlocking(false);

                  analyticsInstance.track("UnlockError", {
                    erc20: tokenBalance.erc20Symbol,
                    tokenx: tokenBalance.tokenxSymbol,
                    amount: amount.toString(),
                    error: err,
                  });

                  console.error(err);
                });

              analyticsInstance.track("UnlockClicked");
            }}
          >
            Approve Spending 🔓
          </Button>

          <Button
            colorScheme="teal"
            size="md"
            ml="0.5em"
            disabled={
              isUnlocking || isUpgrading || amount.eq(0) || amount.gt(useBalanceData.value) || allowance.lt(amount)
            }
            isLoading={isUpgrading}
            onClick={async () => {
              // clear state
              clearState();

              // set loading
              setIsUpgrading(true);

              // upgrade
              let convertedAmount = convertToDecimals(amount, 18, useBalanceData.decimals);
              upgradeErc20(chain.id, signer.data, tokenBalance.tokenxSymbol, convertedAmount)
                .then((result) => {
                  setShowUpgraded(true);
                  setIsUpgrading(false);
                  setAmount(BigNumber.from(0)); // clear the input
                })
                .catch((err) => {
                  setTxError(err);
                  setIsUpgrading(false);

                  analyticsInstance.track("UpgradeError", {
                    tokenx: tokenBalance.tokenxSymbol,
                    amount: convertedAmount.toString(),
                    error: err,
                  });
                });

              analyticsInstance.track("UpgradeClicked");
            }}
          >
            Upgrade 🔺
          </Button>

          <Button
            colorScheme="red"
            size="md"
            onClick={() => {
              if (showUpgraded && setRefetch) setRefetch(showUpgraded);
              clearState();
              inputRef.current.value = "";
              onClose();
            }}
            ml="0.5em"
            isDisabled={isUpgrading}
          >
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

const TokenUpgrader = forwardRef(({ btnLabel, account, tokenBalance, setRefetch, defaultAmount, ...props }, ref) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newOpen, setNewOpen] = useState(true);

  return (
    <>
      <Button
        ref={ref}
        {...props}
        onClick={() => {
          setNewOpen(true);
          onOpen();
          analyticsInstance.track("TokenUpgraderClicked");
        }}
      >
        {btnLabel || "🔺 upgrade"}
      </Button>

      <TokenUpgraderModal
        account={account}
        tokenBalance={tokenBalance}
        setRefetch={setRefetch}
        defaultAmount={defaultAmount || BigNumber.from(0)}
        newOpen={newOpen}
        setNewOpen={setNewOpen}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
});

export { TokenUpgrader, TokenUpgraderModal };
